import { Component, EventEmitter, Inject, Input, Output, ViewEncapsulation } from '@angular/core';
import { PRODUCT_CONTEXT_MENU_PARAMS, ProductContextMenuParams } from '../../services/contextmenu.service';
import { ClipboardService } from 'src/app/services/clipboard.service';
import { LayoutService } from 'src/app/services/layout.service';
import { CommonModule } from '@angular/common';
import { CapabilitiesService } from 'src/app/services/capabilities.service';
import { CapabilityType } from 'src/app/models/capability-type';

/**
 * Product Context Menu Component
 * 
 * This component needs to support the following functionality for the new stack-based structure:
 * 
 * COPY OPERATIONS:
 * - Copy Product - Copy just the specific product that was right-clicked
 * - Copy Stack - Copy the entire stack (all products in the vertical collection)
 * 
 * PASTE OPERATIONS:
 * - Paste Product Above - Add copied product above current product in same stack
 * - Paste Product Below - Add copied product below current product in same stack
 * - Paste Product Before Stack - Create new stack with copied product before current stack
 * - Paste Product After Stack - Create new stack with copied product after current stack
 * - Paste Stack Before - Insert copied stack before current stack
 * - Paste Stack After - Insert copied stack after current stack
 * - Paste Properties - Apply copied product properties to current product
 * 
 * FACING OPERATIONS:
 * Note: In the new stack structure, each product in a stack is limited to 1 facing.
 * Multiple facings require creating adjacent stacks.
 * - Duplicate Stack - Create new stack beside current stack with same product(s)
 * - Remove Facing - Remove this facing from the current stack
 * - Add V Facing - Add same product above current product in same stack (increase stack height)
 * - Remove V Facing - Remove this product from stack (decrease stack height)
 * 
 * PRODUCT MODIFICATION:
 * - Change Orientation (submenu) - Front, Front 90°, Side, Side 90°, Flatpack, Flatpack 90°
 * 
 * STACK ORDER OPERATIONS:
 * - Move Product Up - Move product higher in current stack
 * - Move Product Down - Move product lower in current stack
 * - Move to Top - Move product to top of current stack
 * - Move to Bottom - Move product to bottom of current stack
 * 
 * REMOVAL OPERATIONS:
 * - Remove Product - Remove just this product instance
 * - Remove Stack - Remove entire stack
 * - Clear Stack Above - Remove all products above this one in stack
 * - Clear Stack Below - Remove all products below this one in stack
 * 
 * NAVIGATION/CONTEXT:
 * - Open Shelf Menu - Switch to shelf context menu
 * - Open Stack Menu - New menu type specifically for stack operations
 */

@Component({
    selector: 'app-product-context-menu',
    templateUrl: './product-context-menu.component.html',
    styleUrl: './product-context-menu.component.scss',
    standalone: true,
    imports: [
      CommonModule,
    ],
  encapsulation: ViewEncapsulation.None
})
export class ProductContextMenuComponent {
  @Input() position: { x: number, y: number } = { x: 0, y: 0 };
  
  // Navigation outputs
  @Output() openShelfMenu = new EventEmitter<string>();
  @Output() openStackMenu = new EventEmitter<string>();
  
  // Copy/Paste operations
  @Output() copyProduct = new EventEmitter<string>();
  @Output() copyStack = new EventEmitter<string>();
  @Output() pasteProductAfter = new EventEmitter<string>();
  @Output() pasteProductBefore = new EventEmitter<string>();
  @Output() pasteStackBefore = new EventEmitter<string>();
  @Output() pasteStackAfter = new EventEmitter<string>();
  
  // Modify operations
  @Output() modifyStackHeight = new EventEmitter<string>();
  @Output() modifyProductFacings = new EventEmitter<string>();
  
  // Orientation operations
  @Output() changeOrientation = new EventEmitter<string>();
  
  // Removal operations
  @Output() removeProduct = new EventEmitter<string>();
  @Output() removeStack = new EventEmitter<string>();
  @Output() clearStackAbove = new EventEmitter<string>();
  @Output() clearStackBelow = new EventEmitter<string>();

  expandedMenu: string | null = null;
  
  constructor(
    @Inject(PRODUCT_CONTEXT_MENU_PARAMS) public params: ProductContextMenuParams,
    private clipboardService: ClipboardService,
    private layoutService: LayoutService,
    private capabilitiesService: CapabilitiesService
  ) {}

  get canEdit(): boolean {
    return this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM);
  }

  toggleSubmenu(menu: string, event: Event) {
    event.stopPropagation();
    this.expandedMenu = this.expandedMenu === menu ? null : menu;
  }

  // Helper methods to determine state
  get hasClipboardProduct(): boolean {
    return this.clipboardService.hasCopiedProduct();
  }

  get hasClipboardStack(): boolean {
    // For now, we'll use the same check as product since we don't have separate stack clipboard
    return this.clipboardService.hasCopiedShelf();
  }

  get canMoveUp(): boolean {
    const stack = this.layoutService.getStack(
      this.params.dropIndex, 
      this.params.shelfIndex, 
      this.params.stackIndex
    );
    return stack ? this.params.productIndex > 0 : false;
  }

  get canMoveDown(): boolean {
    const stack = this.layoutService.getStack(
      this.params.dropIndex, 
      this.params.shelfIndex, 
      this.params.stackIndex
    );
    return stack ? this.params.productIndex < stack.products.length - 1 : false;
  }

  get canClearAbove(): boolean {
    return this.params.productIndex > 0;
  }

  get canClearBelow(): boolean {
    const stack = this.layoutService.getStack(
      this.params.dropIndex, 
      this.params.shelfIndex, 
      this.params.stackIndex
    );
    return stack ? this.params.productIndex < stack.products.length - 1 : false;
  }

  get isTopProduct(): boolean {
    return this.params.productIndex === 0;
  }

  get isBottomProduct(): boolean {
    const stack = this.layoutService.getStack(
      this.params.dropIndex, 
      this.params.shelfIndex, 
      this.params.stackIndex
    );
    return stack ? this.params.productIndex === stack.products.length - 1 : false;
  }

  get isSingleProductInStack(): boolean {
    const stack = this.layoutService.getStack(
      this.params.dropIndex, 
      this.params.shelfIndex, 
      this.params.stackIndex
    );
    return stack ? stack.products.length === 1 : false;
  }
}